/**
 * CheckoutReturnController
 *
 * Mounted on /paiement-retour.html. Page transitoire entre le retour
 * Qonto Pay (succès ou annulation) et la confirmation backend
 * (webhook payment.succeeded asynchrone, sous-EPIC 3.4).
 *
 * 4 états visuels (sections déjà présentes dans la maquette, togglées
 * via .hidden / aria-hidden) :
 *   1. loading  — visible par défaut, spinner + "Validation en cours…"
 *   2. success  — webhook confirmé ; redirige vers /tableau-de-bord.html après ~1s.
 *   3. error    — Qonto a renvoyé `?status=cancelled` ou erreur explicite.
 *   4. timeout  — 30 s sans confirmation : message rassurant + CTA dashboard.
 *
 * Comportement (EPIC-003 D3) :
 *   - Au mount : lit ?status= et ?intent_id= dans la query string.
 *   - Si ?status=cancelled (ou ?status=error) : bascule sur l'état error.
 *   - Sinon : démarre un poll setInterval(2s) de GET /billing/subscriptions/me.
 *     - Dès subscription !== null && subscription.status === 'active'
 *       → showSuccess() puis window.location = /tableau-de-bord.html?welcome=1.
 *     - Si Date.now() - start > TIMEOUT_MS (30s) → showTimeout().
 *   - Cleanup obligatoire de l'intervalle dans disconnect().
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const POLL_INTERVAL_MS = 2000;
const TIMEOUT_MS = 30_000;
const SUCCESS_REDIRECT_DELAY_MS = 1500;

export default class CheckoutReturnController extends BaseController {
  static targets = ["loading", "success", "error", "timeout"];

  connect() {
    this.pollHandle = null;
    this.redirectHandle = null;

    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");

    if (status === "cancelled" || status === "error") {
      this.showError();
      return;
    }

    this.startPoll();
  }

  startPoll() {
    this.showLoading();
    const start = Date.now();

    // 1er tick immédiat — si le webhook est déjà arrivé (ex. stub ultra-
    // rapide), on évite d'attendre 2 s pour rien.
    this.pollOnce(start);

    this.pollHandle = setInterval(() => this.pollOnce(start), POLL_INTERVAL_MS);
  }

  async pollOnce(start) {
    if (Date.now() - start > TIMEOUT_MS) {
      this.stopPoll();
      this.showTimeout();
      return;
    }

    try {
      const data = await api.getSubscriptionsMe();
      if (data && data.subscription !== null && data.subscription.status === "active") {
        this.stopPoll();
        this.showSuccessThenRedirect();
      }
      // sinon : on continue le poll
    } catch (_error) {
      // 401 : api.js redirige déjà. Pour toute autre erreur transient
      // (réseau, 5xx ponctuel) on continue de poller jusqu'au timeout.
    }
  }

  stopPoll() {
    if (this.pollHandle !== null) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  showLoading() {
    this.toggleStates({ loading: true, success: false, error: false, timeout: false });
  }

  showSuccessThenRedirect() {
    this.toggleStates({ loading: false, success: true, error: false, timeout: false });
    this.redirectHandle = setTimeout(() => {
      window.location.assign("/tableau-de-bord.html?welcome=1");
    }, SUCCESS_REDIRECT_DELAY_MS);
  }

  showError() {
    this.toggleStates({ loading: false, success: false, error: true, timeout: false });
  }

  showTimeout() {
    this.toggleStates({ loading: false, success: false, error: false, timeout: true });
  }

  /**
   * Toggle les 4 sections d'état. Utilise .hidden + aria-hidden pour
   * l'accessibilité (lecteurs d'écran ignorent les sections cachées).
   * @param {{loading: boolean, success: boolean, error: boolean, timeout: boolean}} states
   */
  toggleStates(states) {
    const apply = (target, visible) => {
      if (!target) return;
      if (visible) {
        target.hidden = false;
        target.removeAttribute("aria-hidden");
      } else {
        target.hidden = true;
        target.setAttribute("aria-hidden", "true");
      }
    };
    if (this.hasLoadingTarget) apply(this.loadingTarget, states.loading);
    if (this.hasSuccessTarget) apply(this.successTarget, states.success);
    if (this.hasErrorTarget) apply(this.errorTarget, states.error);
    if (this.hasTimeoutTarget) apply(this.timeoutTarget, states.timeout);
  }

  disconnect() {
    this.stopPoll();
    if (this.redirectHandle !== null) {
      clearTimeout(this.redirectHandle);
      this.redirectHandle = null;
    }
  }
}
